import cn from "classnames";
import { useCallback, useRef } from "react";
import tippy, { type Instance } from "tippy.js";
import i18n, { normalizeLanguage } from "@/i18n";
import { capitalize } from "../../lib";
import type { EmojiItem } from "../../model";
import { getEmojiSymbolById } from "../Emoji";
import styles from "./EmojiPanelItem.module.scss";

type Props = {
    emoji: EmojiItem;
    onClick: (emoji: EmojiItem) => void;
    disableTooltip?: boolean;
    size: "s" | "m";
};

export function EmojiPanelItem({ emoji, onClick, disableTooltip, size }: Props) {
    const instanceRef = useRef<Instance | null>(null);

    const emojiRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (instanceRef.current) {
                instanceRef.current.destroy();
                instanceRef.current = null;
            }

            if (node && !disableTooltip) {
                const isRu = normalizeLanguage(i18n.language) === "ru";
                const tooltipText = isRu
                    ? emoji?.descriptionLocal || emoji.description
                    : emoji.description;

                instanceRef.current = tippy(node, {
                    content: capitalize(tooltipText),
                    placement: "bottom",
                    offset: [4, 0],
                    delay: [800, -1],
                });
            }
        },
        [emoji, disableTooltip],
    );

    const handleClick = () => onClick(emoji);
    const emojiSymbol = getEmojiSymbolById(emoji.id);

    return (
        <div
            ref={emojiRef}
            className={cn(styles.emojiButton, styles[`emojiButtonSize${capitalize(size)}`])}
            onClick={handleClick}
            data-emoji-font="true"
            data-id={emoji.id}
        >
            {emojiSymbol}
        </div>
    );
}
